import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useTable, usePagination, useSortBy, useExpanded, useResizeColumns, useFlexLayout } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import UnarchiveOutlinedIcon from '@material-ui/icons/UnarchiveOutlined';
import { reducer, useEffectToDispatch, fetchApiData, initialState, Sorting } from "@/shared/apiTableHelper"
import { getProgramById } from "@/shared/apiHelper"
import axios from "axios";
import ProgramBreadcrumb from '@/shared/components/ProgramBreadcrumb'

import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap";
import { useDispatch, sendFlashMessage } from "@/shared/components/flash"
import ApiErrorMessage from "@/shared/components/ApiErrorMessage";

const queryClient = new QueryClient()

const DataTable = ({ program }) => {
  const flashDispatch = useDispatch()
  
  const [loading, setLoading] = useState(true)
  const [trigger, setTrigger] = useState(0);

  const onClickUnarchive = (program) => {
    if (!window.confirm(`Are you sure to unarchived sub program ?`)) {
      return;
    }
    setLoading(true)
    let url = `/organization/${program.organization_id}/program/${program.id}/unarchived`;
    axios.post(url)
      .then((res) => {
        if (res.status === 200) {
          flashDispatch(sendFlashMessage('Program has been unarchived', 'alert-success', 'top'))
          setTrigger(Math.floor(Date.now() / 1000))
        }
      })
      .catch(error => {
        console.log(error.response.data);
        flashDispatch(sendFlashMessage(<ApiErrorMessage errors={error.response.data} />, 'alert-danger', 'top'))
      })
      .finally(() => setLoading(false));
  }

  let COLUMNS = [
     {
        Header: "Id",
        accessor: "id",
        width: 60
    },
    {
        Header: "Name",
        accessor: "name",
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ row }) => (<span title="Unarchive"><UnarchiveOutlinedIcon disabled={loading} style={{ cursor: 'pointer' }} onClick={() => onClickUnarchive(row.original)} />Un-Archive</span>)
    }
  ]

  let columns = useMemo(() => COLUMNS, [])

  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 400,
    }),
    []
  )

  const [{ queryPageIndex, queryPageSize, totalCount, queryPageSortBy, queryTrigger }, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${program.organization_id}/program/${program.id}/archived`

  const { isLoading, error, data, isSuccess } = useQuery(
    ['roles', apiUrl, queryPageIndex, queryPageSize, queryPageSortBy, queryTrigger],
    () => fetchApiData(
      {
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const totalPageCount = Math.ceil(totalCount / queryPageSize)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy }
  } = useTable({
    columns,
    data: data?.full ? data.full : [],
    initialState: {
      pageIndex: queryPageIndex,
      pageSize: queryPageSize,
      sortBy: queryPageSortBy,
    },
    manualPagination: true,
    pageCount: data ? totalPageCount : null,
    autoResetSortBy: false,
    autoResetExpanded: false,
    autoResetPage: false,
    defaultColumn,

  },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout,
  );
  const manualPageSize = []

  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, data, trigger });

  if (error) {
    return <p>Error: {JSON.stringify(error)}</p>;
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }
  if (isSuccess)
    return (
      <>
        <div className='table react-table'>
          <table {...getTableProps()} className="table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render('Header')}
                      {column.isSorted ? <Sorting column={column} /> : ''}
                      <div
                        {...column.getResizerProps()}
                        className={`resizer ${column.isResizing ? 'isResizing' : ''
                          }`}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="table table--bordered" {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                const subCount = (row.id.match(/\./g) || []).length
                return (
                  <tr {...row.getRowProps()}>
                    {
                      row.cells.map(cell => {
                        const paddingLeft = subCount * 20
                        return <td {...cell.getCellProps()}><span style={cell.column.Header === '#' ? { paddingLeft: `${paddingLeft}px` } : null}>{cell.render('Cell')}</span></td>
                      })
                    }
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {(rows.length > 0) && (
          <>
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              pageCount={pageCount}
              setPageSize={setPageSize}
              manualPageSize={manualPageSize}
              dataLength={totalCount}
            />
            <div className="pagination justify-content-end mt-2">
              <span>
                Go to page:{' '}
                <input
                  type="number"
                  value={pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(page);
                  }}
                  style={{ width: '100px' }}
                />
              </span>{' '}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </>
    )
}

const Archive = ({ organization, program }) => {

  return (
    <Container className="dashboard">
      <Row>
        <Col md={12}>
          <h3 className="page-title">Archived Sub Programs</h3>
          <h3 className="page-subhead subhead"><Link className="" to="/">Home</Link> / <Link className="" to="/program">Programs</Link>  <ProgramBreadcrumb program={program} linkSelf={true} /> / <Link className="" to={`/program/${program.id}/subprograms`}>Sub Programs</Link> / Archived Sub Programs </h3>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card>
            <CardBody>
              <DataTable organization={organization} program={program} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

const TableWrapper = ({ organization, theme, rtl }) => {
  const { programId } = useParams();
  const [programData, setProgramData] = useState(null);
  useEffect(() => {
    if (organization && programId) {
      getProgramById(organization.id, programId, { 'hierarchy': true })
        .then(p => {
          setProgramData(p)
        })
    }
  }, [organization, programId])

  if (!organization || !programData) return 'Loading...'

  return (
    <QueryClientProvider client={queryClient}>
      <Archive organization={organization} program={programData} theme={theme} rtl={rtl} />
    </QueryClientProvider>
  )
}

export default withRouter(connect((state) => ({
  theme: state.theme,
  rtl: state.rtl,
  organization: state.organization
}))(TableWrapper));